import { useCallback, useEffect, useRef, useState } from "react";
import Draggable from "react-draggable";
import { makeStyles } from "@material-ui/core/styles";
import { _groupBy, isEmpty, isEqualObject } from "../../utils/FPEUtils";
import RotateIcon from '@material-ui/icons/Cached';
import { useATCPlan } from "../../store/ATCPlanState";
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from "recoil";
import { elapsedTimeForWeatherState } from "../../store/ElapsedTimeState";
import {
  altitudeContentsListState,
  individualAltByTimeControllerState,
  individualAltitudeWithID,
  setForegroundAltitudeSelector
} from "../../store/MapAltitudeState";

const useStyles = makeStyles({
  root: {
    display: 'flex',
    height: 200,
    pointerEvents: 'auto'

  },
  contentsAltitudeBox: {
    height: '100%',
    position: 'relative'
  },
  altitudePoint: {
    position: 'absolute',
    width: "53px",
    height: "16px",
    bottom: 0,
    left: 0,
    marginTop: '-8px',
    marginBottom: '-8px',
    lineHeight: "16px",
    color: "#ffffff",
    fontWeight: "bold",
    fontSize:'9px',
    cursor: 'pointer',
    '&:before': {
      content: "' '",
      display: "inline-block",
      position: "absolute",
      top: 0,
      left: "-16px",
      width: "0px",
      height: "0px",
      border: "8px solid",
      borderColor: "transparent",
      pointerEvents: 'none'
    }
  },
  WX_ID_UKMET: {
    background: "rgb(0,0,128)",
    '&:before': {
      borderRightColor: "rgb(0,0,128)",
    }
  },
  WX_ID_ASCSCALE: {
    background: "rgb(83,126,153)",
    '&:before': {
      borderRightColor: "rgb(83,126,153)",
    }
  },
  WX_ID_ACOS: {
    background: "rgb(0,100,0)",
    '&:before': {
      borderRightColor: "rgb(0,100,0)",
    }
  },
  rotateIcon: {
    position: 'absolute',
    right: '-18px',
    top: '-3px',
    fontSize: '22px',
    color: '#6495ed'
  },
  lock: {
    position: 'absolute',
    color: 'rgb(0,0,0)',
    fontSize: 16
  },
  label: {
    pointerEvents: 'none'
  },
  tooltip: {
    display: 'none',
    position: "absolute",
    top: -21,
    left: 0,
    height: "16px",
    padding: 2,
    background: 'rgba(22,22,22,0.5)',
    whiteSpace: 'nowrap'
  }
});

const ContentsAltitudeSlider = ({min, max, step}) => {
  const sliderRef = useRef();
  const target = useRef();
  const targetId = useRef();
  const targetTooltip = useRef();
  const classes = useStyles();
  const contentsList = useRecoilValue(altitudeContentsListState);
  const setForeground = useSetRecoilState(setForegroundAltitudeSelector);
  const {atcPlan} = useATCPlan();
  const elapsedTimeForWeather = useRecoilValue(elapsedTimeForWeatherState);
  const onEffectTimeController = useSetRecoilState(individualAltByTimeControllerState);
  const [altitudeState, setAltitudeState] = useState({});
  const [locks, setLocks] = useState([]);

  const onChangeCommitted = useRecoilCallback(({set}) => payload => {
    set(individualAltitudeWithID(payload.id), payload.altitude);
  });

  //タイムスライダーによって高度が変更されている場合はstateを更新する
  const chkUpdate = contentsList.reduce((accumulator, contents) => {
    accumulator[contents.id] = contents.altitude;
    return accumulator;
  }, {});

  if (!isEqualObject(altitudeState, chkUpdate)) {
    setAltitudeState(chkUpdate);
  }
  const overlap = _groupBy(contentsList, 'altitude');

  /**
   * スライダー位置から高度を取得
   */
  const getPosValue = useCallback((pos, round = false) => {
    const bounds = sliderRef.current.getBoundingClientRect();
    const height = bounds.height
    const bottom = bounds.bottom;

    const percent = (bottom - pos) / height;
    const posValue = (max - min) * percent + min;
    let stepValue = (posValue - min) / step * step + min;
    if (round) {
      stepValue = Math.round(stepValue);
    }
    const retValue = Math.min(Math.max(min, stepValue), max);
    return Math.floor(retValue);
  }, [max, min, step]);

  /**
   * 高度からスライダー位置を取得
   */
  const getValuePos = useCallback((value) => {
    const bounds = sliderRef.current.getBoundingClientRect();
    const height = bounds.height;

    const percent =  (value - min) / (max - min);
    const retValue = height * percent * -1;
    return retValue;
  }, [min, max]);

  /**
   * コンテンツクラスのtooltipを呼び出してツールチップに表示する.
   */
  const updateTooltip = useCallback((altitude) => {
    const contents = contentsList.find(contents => {
      return contents.id === targetId.current;
    });
    if (contents.tooltip) {
      targetTooltip.current.innerHTML = contents.tooltip(altitude);
    }
  },[contentsList]);

  /**
   * ドラッグしているスライダーの位置を取得する
   */
  const targetPosition = useCallback(() => {
    const targetBounds = target.current.getBoundingClientRect();
    const pos = targetBounds.top + targetBounds.height / 2;
    return pos;
  },[]);

  /**
   * ドラッグ開始
   */
  const handleDragStart = useCallback(e => {
    target.current = e.currentTarget;
    targetId.current = e.currentTarget.dataset.id;
    targetTooltip.current = e.currentTarget.lastChild;
    targetTooltip.current.style.display = 'block';
    //RotateIcon非表示
    if (e.currentTarget.childElementCount === 3) {
      e.currentTarget.childNodes[1].style.display = 'none';
    }
  }, []);

  /**
   * ドラッグ中
   */
  const handleDragMove = useCallback(_ => {
    const pos = targetPosition();
    const posValue = getPosValue(pos, true);
    updateTooltip(posValue);
  }, [targetPosition, getPosValue, updateTooltip]);

  /**
   * ドラッグ終了
   */
  const handleDragStop = () => {
    const pos = targetPosition();
    const posValue = getPosValue(pos);

    targetTooltip.current.style.display = 'none';
    if (altitudeState[targetId.current] !== posValue) {
      onChangeCommitted({id:targetId.current, altitude:posValue})
      setAltitudeState({...altitudeState, [targetId.current]: posValue});
      return;
    }

    //ポイントを移動するためcontentsListを並び替える
    setForeground(targetId.current)
  };

  /**
   *タイムスライダー連携ロック
   */
  const handleDoubleClick = useCallback(e => {
    const contents = e.currentTarget.dataset.id;
    const _locks = [...locks];
    const idx =_locks.findIndex(c => {return c === contents});
    if (idx !== -1) {
      _locks.splice(idx, 1);
    } else {
      _locks.push(contents);
    }
    setLocks(_locks);
  },[locks, setLocks]);

  //タイムスライダー連携
  useEffect(_ => {
    let altitude;
    if (isEmpty(atcPlan.edit.json)) {
      altitude = 0;
    } else {
      altitude = calcAltitudeBySec(atcPlan.edit.json, elapsedTimeForWeather, min, max);
    }
    onEffectTimeController(altitude);
  }, [atcPlan.edit.json, elapsedTimeForWeather, min, max, onEffectTimeController]);

  return (
    <div className={classes.contentsAltitudeBox} ref={sliderRef}>
      {contentsList.map(contents => {
        const altitude = altitudeState[contents.id]
        const pos = getValuePos(altitude);

        const orderList = overlap[''+altitude];
        let isUpper = false;
        if (orderList && orderList.length > 1) {
          isUpper = orderList[orderList.length -1].id === contents.id;
        }
        return (
          <Draggable key={contents.id} bounds="parent" onStart={handleDragStart} onDrag={handleDragMove} onStop={handleDragStop} position={{x:0, y:pos}}>
            <div data-id={contents.id} className={`${classes.altitudePoint} ${classes[contents.id]}`} onDoubleClick={handleDoubleClick}>
              <span　className={classes.label}>{contents.label}</span>
              {/*TODO タイムスライダー連携ロック*/}
              {/*{locks.includes(contents.id) && <Lock className={classes.lock}/>}*/}
              {isUpper && <RotateIcon className={classes.rotateIcon}/>}
              <span className={classes.tooltip}/>
            </div>
          </Draggable>
        );
      })}
    </div>
  );
};

/**
 * プランの経過時間に対応した高度を計算.
 */
const calcAltitudeBySec = (plan, paramSec, min, max) => {
  let elapsedFromDep = 0;
  const flatData = plan.route.reduce((accumulator, rp) => {
    if (rp.airway) {
      const aPoints = rp.airway.reduce((ac, ap) => {
        if (ap.time !== 0){
          elapsedFromDep += ap.time;
          ac.push({...ap, elapsedFromDep: elapsedFromDep});
        }
        return ac;
      }, []);
      accumulator.push(aPoints);
    } else if (rp.time !== 0) {
      elapsedFromDep += rp.time;
      accumulator.push({...rp, elapsedFromDep: elapsedFromDep});
    }
    return accumulator;
  },[]).flat();

  flatData.unshift({...plan.dep, level:0, elapsedFromDep: 0});
  //routeの最後がAirwayで到着空港と同じ場合の対処
  const tmpLastFix = flatData[flatData.length - 1];
  if(plan.arr.icao === tmpLastFix.name && plan.arr.lat === tmpLastFix.lat && plan.arr.lon === tmpLastFix.lon) {
    flatData.pop();
  }
  flatData.push({...plan.arr, level:0, elapsedFromDep: elapsedFromDep += plan.arr.time});

  let prevData;
  let alt;
  for(let i = 0; i < flatData.length; i++) {
    const data = flatData[i];
    const dataSec = data.elapsedFromDep;
    if(dataSec === paramSec) {
      alt = data.level;
      break;
    } else if(dataSec < paramSec && i !== flatData.length -1) {
      prevData = data;
    } else {
      const proportion = (paramSec - prevData.elapsedFromDep) / (data.elapsedFromDep - prevData.elapsedFromDep);
      alt = prevData.level + (data.level - prevData.level) * proportion;
      break;
    }
  }

  return Math.min(Math.max(min, Math.floor(alt / 100)), max);
}

export default ContentsAltitudeSlider;